import { api } from "@infrastructure/api";
import { ConversationSchema, MessageSchema, } from "../models";
function nullableString(value) {
    if (value == null)
        return value;
    return String(value);
}
function normalizeParticipant(item) {
    if (!item || typeof item !== "object")
        return item;
    const record = item;
    return {
        ...record,
        id: nullableString(record.id ?? record.user_id ?? record.org_id ?? record.orgId),
        name: record.name ??
            record.display_name ??
            record.org_name ??
            record.orgName ??
            "Unknown participant",
        org_id: nullableString(record.org_id ?? record.orgId),
        org_name: record.org_name ?? record.orgName,
        org_type: record.org_type ?? record.orgType,
    };
}
function normalizePeerIdentity(item) {
    if (!item || typeof item !== "object")
        return item;
    const record = item;
    return {
        ...record,
        id: nullableString(record.id ?? record.user_id ?? record.org_id ?? record.orgId),
        org_id: nullableString(record.org_id ?? record.orgId),
        org_name: record.org_name ?? record.orgName,
        org_type: record.org_type ?? record.orgType,
    };
}
function normalizeConversation(item) {
    if (!item || typeof item !== "object")
        return item;
    const record = item;
    const participants = record.participants ??
        record.conversation_participants ??
        record.participant_orgs ??
        [];
    return {
        ...record,
        concierge_org_id: nullableString(record.concierge_org_id ?? record.conciergeOrgId),
        provider_org_id: nullableString(record.provider_org_id ?? record.providerOrgId),
        attachment_mime_type: nullableString(record.attachment_mime_type ?? record.attachmentMimeType),
        attachment_size: record.attachment_size ?? record.attachmentSize,
        last_message_preview: record.last_message_preview ?? record.lastMessagePreview ?? null,
        peer_identity: normalizePeerIdentity(record.peer_identity ?? record.peerIdentity),
        peer: normalizePeerIdentity(record.peer),
        participants: Array.isArray(participants)
            ? participants.map(normalizeParticipant)
            : participants,
    };
}
function normalizeMessage(item) {
    if (!item || typeof item !== "object")
        return item;
    const record = item;
    const senderOrganization = (record.sender_organization ??
        record.senderOrganization);
    return {
        ...record,
        sender_org_id: nullableString(record.sender_org_id ?? record.senderOrgId),
        sender_org_name: record.sender_org_name ??
            record.senderOrgName ??
            senderOrganization?.display_name ??
            senderOrganization?.name,
        sender_org_type: record.sender_org_type ??
            record.senderOrgType ??
            senderOrganization?.org_type ??
            senderOrganization?.orgType,
        concierge_org_id: nullableString(record.concierge_org_id ?? record.conciergeOrgId),
        provider_org_id: nullableString(record.provider_org_id ?? record.providerOrgId),
        peer_identity: normalizePeerIdentity(record.peer_identity ?? record.peerIdentity),
        sender: normalizePeerIdentity(record.sender),
    };
}
export const conversationRepository = {
    async getConversations() {
        const response = await api.get("/conversations");
        return response.data.map((item) => ConversationSchema.parse(normalizeConversation(item)));
    },
    async getConversation(id) {
        const response = await api.get(`/conversations/${id}`);
        return ConversationSchema.parse(normalizeConversation(response.data));
    },
    async createConversation(payload) {
        const dto = {
            conversation_type: payload.conversation_type.toLowerCase(),
            service_id: Number(payload.service_id),
            ...(payload.itinerary_id != null && {
                itinerary_id: Number(payload.itinerary_id),
            }),
            ...(payload.booking_id != null &&
                payload.booking_id !== "" && {
                booking_id: Number(payload.booking_id),
            }),
            ...(payload.slot_id != null &&
                payload.slot_id !== "" && { slot_id: Number(payload.slot_id) }),
        };
        const response = await api.post("/conversations", dto);
        return ConversationSchema.parse(normalizeConversation(response.data));
    },
    async getMessages(conversationId) {
        const response = await api.get(`/conversations/${conversationId}/messages`);
        return response.data.map((item) => MessageSchema.parse(normalizeMessage(item)));
    },
    async sendMessage(conversationId, payload) {
        const response = await api.post(`/conversations/${conversationId}/messages`, payload);
        return MessageSchema.parse(normalizeMessage(response.data));
    },
    async closeConversation(conversationId) {
        const response = await api.post(`/conversations/${conversationId}/close`);
        return ConversationSchema.parse(normalizeConversation(response.data));
    },
    async editMessage(messageId, text) {
        const response = await api.patch(`/messages/${messageId}`, {
            message: text,
        });
        return MessageSchema.parse(normalizeMessage(response.data));
    },
    async deleteMessage(messageId) {
        await api.delete(`/messages/${messageId}`);
    },
    async getUnreadMessageNotificationCount() {
        const response = await api.get("/conversations/message-notifications/unread-count");
        return response.data ?? { count: 0 };
    },
    async markMessageNotificationsRead(conversationId) {
        const response = await api.post(`/conversations/${conversationId}/read-notifications`);
        return response.data ?? { updated: 0 };
    },
};
export default conversationRepository;
